import React, { useEffect, useState, useMemo, useCallback } from 'react';
import axios from 'axios';
import Header from '../components/Header';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { HelpCircle, Send, Clock, CheckCircle, XCircle, AlertCircle, FileText, UserCheck, Building, DollarSign, Upload, ShieldCheck, Image, Filter, MessageSquare } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SelfHelpPage = ({ user, setUser }) => {
  const [categories, setCategories] = useState({});
  const [selectedCategory, setSelectedCategory] = useState('');
  const [formData, setFormData] = useState({});
  const [screenshot, setScreenshot] = useState(null);
  const [screenshotFilename, setScreenshotFilename] = useState('');
  const [myRequests, setMyRequests] = useState([]);
  const [pendingApprovals, setPendingApprovals] = useState([]);
  const [entitledAmount, setEntitledAmount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [approvalNotes, setApprovalNotes] = useState('');
  
  // Filter states for My Requests
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  const fetchData = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const [categoriesRes, requestsRes, approvalsRes] = await Promise.all([
        axios.get(`${API}/self-help/categories`, config),
        axios.get(`${API}/self-help/requests`, config),
        axios.get(`${API}/self-help/pending-approvals`, config).catch(() => ({ data: [] }))
      ]);

      setCategories(categoriesRes.data);
      setMyRequests(requestsRes.data);
      setPendingApprovals(approvalsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchEntitledAmount = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };
      const response = await axios.get(`${API}/self-help/entitled-amount`, config);
      setEntitledAmount(response.data);
    } catch (error) {
      console.error('Failed to fetch entitled amount');
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (selectedCategory === 'Advance Salary Request') {
      fetchEntitledAmount();
    }
  }, [selectedCategory, fetchEntitledAmount]);

  const handleCategoryChange = (category) => {
    setSelectedCategory(category);
    setFormData({});
    setScreenshot(null);
    setScreenshotFilename('');
  };

  const handleFieldChange = (fieldName, value) => {
    setFormData(prev => ({ ...prev, [fieldName]: value }));
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error('File size exceeds 10MB limit');
      return;
    }

    // Validate file type (images only)
    if (!file.type.startsWith('image/')) {
      toast.error('Only image files are allowed');
      return;
    }

    // Convert to base64
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:image/...;base64, prefix
      setScreenshot(base64);
      setScreenshotFilename(file.name);
      toast.success('Screenshot uploaded successfully');
    };
    reader.onerror = () => {
      toast.error('Failed to read file');
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!selectedCategory) {
      toast.error('Please select a category');
      return;
    }

    // Validate required fields (except file type fields)
    const categoryFields = categories[selectedCategory]?.fields || [];
    for (const field of categoryFields) {
      if (field.required && field.type !== 'readonly' && field.type !== 'file' && !formData[field.name]) {
        toast.error(`Please fill in ${field.label}`);
        return;
      }
    }

    // For Advance Salary, validate amount
    if (selectedCategory === 'Advance Salary Request' && entitledAmount) {
      const requestedAmount = parseFloat(formData.amount || 0);
      if (requestedAmount > entitledAmount.entitled_amount) {
        toast.error(`Amount cannot exceed entitled amount of ₹${entitledAmount.entitled_amount.toLocaleString('en-IN')}`);
        return;
      }
      if (requestedAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        category: selectedCategory,
        form_data: formData
      };

      // Include screenshot if uploaded
      if (screenshot) {
        payload.screenshot = screenshot;
        payload.screenshot_filename = screenshotFilename;
      }

      await axios.post(`${API}/self-help/requests`, payload, config);

      toast.success('Request submitted successfully!');
      setSelectedCategory('');
      setFormData({});
      setScreenshot(null);
      setScreenshotFilename('');
      setEntitledAmount(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (requestId, action) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      // Use the generic approval endpoint
      await axios.post(`${API}/self-help/approve`, {
        request_id: requestId,
        action: action,
        notes: approvalNotes
      }, config);

      toast.success(`Request ${action === 'approve' ? 'approved' : 'rejected'} successfully!`);
      setSelectedRequest(null);
      setApprovalNotes('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to process approval');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'pending': { bg: 'bg-yellow-50', text: 'text-yellow-700', border: 'border-yellow-300', icon: Clock, label: 'Pending' },
      'pending_rm': { bg: 'bg-orange-50', text: 'text-orange-700', border: 'border-orange-300', icon: UserCheck, label: 'Pending RM Approval' },
      'pending_hr': { bg: 'bg-purple-50', text: 'text-purple-700', border: 'border-purple-300', icon: Building, label: 'Pending HR Approval' },
      'pending_payroll': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', icon: DollarSign, label: 'Pending Payroll' },
      'pending_admin': { bg: 'bg-indigo-50', text: 'text-indigo-700', border: 'border-indigo-300', icon: ShieldCheck, label: 'Pending Admin Approval' },
      'in_progress': { bg: 'bg-blue-50', text: 'text-blue-700', border: 'border-blue-300', icon: AlertCircle, label: 'In Progress' },
      'resolved': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', icon: CheckCircle, label: 'Resolved' },
      'completed': { bg: 'bg-green-50', text: 'text-green-700', border: 'border-green-300', icon: CheckCircle, label: 'Completed' },
      'rejected': { bg: 'bg-red-50', text: 'text-red-700', border: 'border-red-300', icon: XCircle, label: 'Rejected' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    const IconComponent = config.icon;
    
    return (
      <Badge variant="outline" className={`${config.bg} ${config.text} ${config.border}`}>
        <IconComponent className="w-3 h-3 mr-1" />
        {config.label}
      </Badge>
    );
  };

  const renderFormField = (field) => {
    const value = formData[field.name] || '';

    if (field.type === 'readonly') {
      if (field.name === 'entitled_amount' && entitledAmount) {
        return (
          <div className="p-3 bg-green-50 border border-green-200 rounded-md">
            <span className="text-lg font-bold text-green-700">
              ₹{entitledAmount.entitled_amount?.toLocaleString('en-IN') || '0'}
            </span>
            <span className="text-sm text-gray-500 ml-2">
              (90% of ₹{entitledAmount.fixed_monthly_salary?.toLocaleString('en-IN') || '0'})
            </span>
          </div>
        );
      }
      if (field.name === 'repayment_months') {
        return (
          <div className="p-3 bg-gray-100 border border-gray-200 rounded-md">
            <span className="font-semibold">3 Months</span>
            <span className="text-sm text-gray-500 ml-2">(Fixed)</span>
          </div>
        );
      }
      return (
        <Input value={field.value || ''} disabled className="bg-gray-100" />
      );
    }

    switch (field.type) {
      case 'text':
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            required={field.required}
            data-testid={`field-${field.name}`}
          />
        );
      case 'number':
        return (
          <div>
            <Input
              type="number"
              value={value}
              onChange={(e) => handleFieldChange(field.name, e.target.value)}
              placeholder={`Enter ${field.label.toLowerCase()}`}
              required={field.required}
              max={field.max_entitled && entitledAmount ? entitledAmount.entitled_amount : undefined}
              data-testid={`field-${field.name}`}
            />
            {field.max_entitled && entitledAmount && (
              <p className="text-xs text-gray-500 mt-1">
                Maximum: ₹{entitledAmount.entitled_amount?.toLocaleString('en-IN')}
              </p>
            )}
          </div>
        );
      case 'date':
        return (
          <Input
            type="date"
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            required={field.required}
            data-testid={`field-${field.name}`}
          />
        );
      case 'textarea':
        return (
          <Textarea
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            rows={3}
            required={field.required}
            data-testid={`field-${field.name}`}
          />
        );
      case 'select':
        return (
          <Select value={value || undefined} onValueChange={(val) => handleFieldChange(field.name, val)}>
            <SelectTrigger data-testid={`field-${field.name}`}>
              <SelectValue placeholder={`Select ${field.label.toLowerCase()}`} />
            </SelectTrigger>
            <SelectContent>
              {field.options.map(option => (
                <SelectItem key={option} value={option}>{option}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      case 'file':
        return (
          <div className="space-y-2">
            <div className="flex items-center gap-2">
              <label 
                htmlFor={`file-${field.name}`}
                className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50 transition-colors"
              >
                <Upload className="w-4 h-4 text-gray-500" />
                <span className="text-sm text-gray-600">Choose File</span>
              </label>
              <input
                id={`file-${field.name}`}
                type="file"
                accept={field.accept || 'image/*'}
                onChange={handleFileUpload}
                className="hidden"
                data-testid={`field-${field.name}`}
              />
              {screenshotFilename && (
                <span className="text-sm text-green-600 flex items-center gap-1">
                  <Image className="w-4 h-4" />
                  {screenshotFilename}
                </span>
              )}
            </div>
            <p className="text-xs text-gray-500">
              Max file size: {field.max_size_mb || 10}MB. Supported formats: Images (PNG, JPG, etc.)
            </p>
          </div>
        );
      default:
        return (
          <Input
            value={value}
            onChange={(e) => handleFieldChange(field.name, e.target.value)}
            placeholder={`Enter ${field.label.toLowerCase()}`}
            required={field.required}
          />
        );
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Helper to get all notes from a request
  const getAllNotes = (request) => {
    const notes = [];
    if (request.rm_notes) notes.push({ role: 'RM', note: request.rm_notes, approved: request.rm_approved });
    if (request.hr_notes) notes.push({ role: 'HR', note: request.hr_notes, approved: request.hr_approved });
    if (request.payroll_notes) notes.push({ role: 'Payroll', note: request.payroll_notes, approved: request.payroll_approved });
    if (request.admin_notes) notes.push({ role: 'Admin', note: request.admin_notes, approved: request.admin_approved });
    return notes;
  };

  // Filtered requests based on filters
  const filteredMyRequests = useMemo(() => {
    return myRequests
      .filter(r => r.employee_id === user.id)
      .filter(r => categoryFilter === 'all' || r.category === categoryFilter)
      .filter(r => statusFilter === 'all' || r.status === statusFilter);
  }, [myRequests, user.id, categoryFilter, statusFilter]);

  // Get unique statuses for filter
  const uniqueStatuses = useMemo(() => {
    const statuses = new Set(myRequests.filter(r => r.employee_id === user.id).map(r => r.status));
    return Array.from(statuses);
  }, [myRequests, user.id]);

  const getApprovalTimeline = (request) => {
    const timeline = [];
    
    timeline.push({
      label: 'Submitted',
      date: request.created_at,
      status: 'completed'
    });
    
    if (request.rm_approved !== null) {
      timeline.push({
        label: request.rm_approved ? 'RM Approved' : 'RM Rejected',
        date: request.rm_approved_at,
        status: request.rm_approved ? 'completed' : 'rejected',
        notes: request.rm_notes
      });
    } else if (request.status === 'pending_rm') {
      timeline.push({ label: 'Pending RM Approval', status: 'pending' });
    }
    
    if (request.hr_approved !== null) {
      timeline.push({
        label: request.hr_approved ? 'HR Approved' : 'HR Rejected',
        date: request.hr_approved_at,
        status: request.hr_approved ? 'completed' : 'rejected',
        notes: request.hr_notes
      });
    } else if (request.status === 'pending_hr') {
      timeline.push({ label: 'Pending HR Approval', status: 'pending' });
    }
    
    if (request.payroll_approved !== null) {
      timeline.push({
        label: request.payroll_approved ? 'Payroll Approved' : 'Payroll Rejected',
        date: request.payroll_approved_at,
        status: request.payroll_approved ? 'completed' : 'rejected',
        notes: request.payroll_notes
      });
    } else if (request.status === 'pending_payroll') {
      timeline.push({ label: 'Pending Payroll Approval', status: 'pending' });
    }
    
    // Admin approval (for RM -> Admin workflow)
    if (request.admin_approved !== null) {
      timeline.push({
        label: request.admin_approved ? 'Admin Approved' : 'Admin Rejected',
        date: request.admin_approved_at,
        status: request.admin_approved ? 'completed' : 'rejected',
        notes: request.admin_notes
      });
    } else if (request.status === 'pending_admin') {
      timeline.push({ label: 'Pending Admin Approval', status: 'pending' });
    }
    
    if (request.status === 'completed') {
      timeline.push({
        label: 'Completed',
        date: request.resolved_at,
        status: 'completed'
      });
    }
    
    return timeline;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-purple-50" data-testid="self-help-page">
      <Header user={user} setUser={setUser} />
      <Navigation user={user} />
      
      <div className="px-4 sm:px-6 lg:px-8 py-6 max-w-screen-2xl mx-auto w-full">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2 flex items-center gap-2">
            <HelpCircle className="w-8 h-8 text-purple-600" />
            Self Help Portal
          </h1>
          <p className="text-gray-600">Submit requests for assistance across various categories</p>
        </div>

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <div className="space-y-6">
            {/* Pending Approvals Section - For RM/HR/Payroll/Admin */}
            {pendingApprovals.length > 0 && (
              <Card className="shadow-lg border-orange-200 bg-orange-50" data-testid="pending-approvals-card">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-orange-800">
                    <UserCheck className="w-5 h-5" />
                    Pending Your Approval ({pendingApprovals.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {pendingApprovals.map(request => (
                      <div 
                        key={request.id} 
                        className="p-4 bg-white border rounded-lg shadow-sm"
                        data-testid={`approval-${request.id}`}
                      >
                        <div className="flex justify-between items-start mb-2">
                          <div>
                            <h4 className="font-semibold text-gray-900">{request.employee_name}</h4>
                            <p className="text-sm text-gray-500">{request.category}</p>
                          </div>
                          <Badge className="bg-orange-100 text-orange-700">
                            {request.approval_role === 'rm' ? 'RM Approval' : 
                             request.approval_role === 'hr' ? 'HR Approval' : 
                             request.approval_role === 'admin' ? 'Admin Approval' : 'Payroll Approval'}
                          </Badge>
                        </div>
                        <div className="text-sm text-gray-600 mb-3">
                          {request.category === 'Advance Salary Request' ? (
                            <>
                              <p><strong>Amount:</strong> ₹{request.form_data?.amount?.toLocaleString('en-IN')}</p>
                              <p><strong>Reason:</strong> {request.form_data?.reason}</p>
                            </>
                          ) : (
                            <>
                              {Object.entries(request.form_data || {}).slice(0, 3).map(([key, value]) => (
                                value && key !== 'screenshot' && (
                                  <p key={key}><strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value}</p>
                                )
                              ))}
                            </>
                          )}
                          <p><strong>Submitted:</strong> {formatDate(request.created_at)}</p>
                          {request.screenshot_filename && (
                            <p className="flex items-center gap-1 text-blue-600">
                              <Image className="w-3 h-3" />
                              Screenshot attached: {request.screenshot_filename}
                            </p>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <Button 
                            size="sm" 
                            className="bg-green-600 hover:bg-green-700"
                            onClick={() => setSelectedRequest({...request, action: 'approve'})}
                            data-testid={`approve-btn-${request.id}`}
                          >
                            <CheckCircle className="w-4 h-4 mr-1" /> Approve
                          </Button>
                          <Button 
                            size="sm" 
                            variant="destructive"
                            onClick={() => setSelectedRequest({...request, action: 'reject'})}
                            data-testid={`reject-btn-${request.id}`}
                          >
                            <XCircle className="w-4 h-4 mr-1" /> Reject
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Submit New Request */}
              <Card className="shadow-lg" data-testid="new-request-card">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-indigo-50">
                  <CardTitle className="flex items-center gap-2">
                    <Send className="w-5 h-5 text-purple-600" />
                    Submit New Request
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-6">
                  <form onSubmit={handleSubmit} className="space-y-4">
                    {/* Category Dropdown */}
                    <div>
                      <Label className="text-base font-semibold">Select Category *</Label>
                      <Select value={selectedCategory || undefined} onValueChange={handleCategoryChange}>
                        <SelectTrigger className="mt-2" data-testid="category-select">
                          <SelectValue placeholder="Choose a category..." />
                        </SelectTrigger>
                        <SelectContent>
                          {Object.keys(categories).sort().map(category => (
                            <SelectItem key={category} value={category}>
                              {category}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Dynamic Form Fields */}
                    {selectedCategory && categories[selectedCategory] && (
                      <div className="space-y-4 pt-4 border-t">
                        <p className="text-sm text-gray-600 italic">
                          {categories[selectedCategory].description}
                        </p>
                        
                        {categories[selectedCategory].has_approval_workflow && (
                          <div className="p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <p className="text-sm text-blue-800">
                              <strong>Approval Workflow:</strong> {
                                selectedCategory === 'Advance Salary Request' 
                                  ? 'Reporting Manager → HR Partner → Payroll'
                                  : 'Reporting Manager → Administrator'
                              }
                            </p>
                          </div>
                        )}
                        
                        {categories[selectedCategory].fields.map(field => (
                          <div key={field.name}>
                            <Label className="flex items-center gap-1">
                              {field.label}
                              {field.required && field.type !== 'readonly' && field.type !== 'file' && <span className="text-red-500">*</span>}
                            </Label>
                            <div className="mt-1">
                              {renderFormField(field)}
                            </div>
                          </div>
                        ))}

                        <Button 
                          type="submit" 
                          className="w-full bg-purple-600 hover:bg-purple-700 mt-4"
                          disabled={submitting}
                          data-testid="submit-request-btn"
                        >
                          {submitting ? 'Submitting...' : 'Submit Request'}
                        </Button>
                      </div>
                    )}
                  </form>
                </CardContent>
              </Card>

              {/* My Requests */}
              <Card className="shadow-lg" data-testid="my-requests-card">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-cyan-50">
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    My Requests ({filteredMyRequests.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="pt-4">
                  {/* Filters */}
                  <div className="flex gap-3 mb-4 p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center gap-2">
                      <Filter className="w-4 h-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Filters:</span>
                    </div>
                    <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                      <SelectTrigger className="w-[180px] h-8 text-sm" data-testid="category-filter">
                        <SelectValue placeholder="All Categories" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Categories</SelectItem>
                        {Object.keys(categories).sort().map(cat => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Select value={statusFilter} onValueChange={setStatusFilter}>
                      <SelectTrigger className="w-[160px] h-8 text-sm" data-testid="status-filter">
                        <SelectValue placeholder="All Status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Status</SelectItem>
                        {uniqueStatuses.map(status => (
                          <SelectItem key={status} value={status}>
                            {status.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {(categoryFilter !== 'all' || statusFilter !== 'all') && (
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        onClick={() => { setCategoryFilter('all'); setStatusFilter('all'); }}
                        className="text-xs"
                      >
                        Clear
                      </Button>
                    )}
                  </div>

                  {filteredMyRequests.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <FileText className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No requests found</p>
                      <p className="text-sm">
                        {myRequests.filter(r => r.employee_id === user.id).length > 0 
                          ? 'Try adjusting your filters' 
                          : 'Select a category and submit your first request'}
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3 max-h-[500px] overflow-y-auto">
                      {filteredMyRequests.map(request => (
                        <div 
                          key={request.id} 
                          className="p-4 border rounded-lg hover:bg-gray-50 transition-colors cursor-pointer"
                          onClick={() => setSelectedRequest({...request, viewOnly: true})}
                          data-testid={`request-${request.id}`}
                        >
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-semibold text-gray-900">{request.category}</h4>
                            {getStatusBadge(request.status)}
                          </div>
                          <p className="text-sm text-gray-500 mb-2">
                            Submitted: {formatDate(request.created_at)}
                          </p>
                          {request.category === 'Advance Salary Request' && (
                            <div className="text-sm text-gray-600">
                              <p><strong>Amount:</strong> ₹{request.form_data?.amount?.toLocaleString('en-IN')}</p>
                            </div>
                          )}
                          {/* Display ALL notes from all approvers */}
                          {getAllNotes(request).length > 0 && (
                            <div className="mt-2 space-y-1">
                              {getAllNotes(request).map((noteItem, idx) => (
                                <div 
                                  key={idx} 
                                  className={`p-2 rounded text-sm flex items-start gap-2 ${
                                    noteItem.approved === false ? 'bg-red-50' : 'bg-blue-50'
                                  }`}
                                >
                                  <MessageSquare className={`w-3 h-3 mt-0.5 ${
                                    noteItem.approved === false ? 'text-red-500' : 'text-blue-500'
                                  }`} />
                                  <div>
                                    <span className="font-medium">{noteItem.role}:</span> {noteItem.note}
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          </div>
        )}
      </div>

      {/* Approval/View Dialog */}
      <Dialog open={!!selectedRequest} onOpenChange={() => { setSelectedRequest(null); setApprovalNotes(''); }}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {selectedRequest?.viewOnly ? 'Request Details' : 
               selectedRequest?.action === 'approve' ? 'Approve Request' : 'Reject Request'}
            </DialogTitle>
          </DialogHeader>
          
          {selectedRequest && (
            <div className="space-y-4">
              <div className="space-y-2">
                <p><strong>Category:</strong> {selectedRequest.category}</p>
                <p><strong>Employee:</strong> {selectedRequest.employee_name}</p>
                <p><strong>Submitted:</strong> {formatDate(selectedRequest.created_at)}</p>
                {selectedRequest.form_data?.amount && (
                  <p><strong>Amount:</strong> ₹{selectedRequest.form_data.amount.toLocaleString('en-IN')}</p>
                )}
                {selectedRequest.form_data?.reason && (
                  <p><strong>Reason:</strong> {selectedRequest.form_data.reason}</p>
                )}
                {/* Display other form data for non-advance salary requests */}
                {selectedRequest.category !== 'Advance Salary Request' && (
                  <div className="space-y-1">
                    {Object.entries(selectedRequest.form_data || {}).map(([key, value]) => (
                      value && key !== 'screenshot' && (
                        <p key={key}>
                          <strong>{key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}:</strong> {value}
                        </p>
                      )
                    ))}
                  </div>
                )}
                {selectedRequest.screenshot_filename && (
                  <p className="flex items-center gap-1 text-blue-600">
                    <Image className="w-4 h-4" />
                    Screenshot: {selectedRequest.screenshot_filename}
                  </p>
                )}
              </div>
              
              {/* Approval Timeline - Show for all categories with approval workflow */}
              {categories[selectedRequest.category]?.has_approval_workflow && (
                <div className="border-t pt-4">
                  <h4 className="font-semibold mb-2">Approval Timeline</h4>
                  <div className="space-y-2">
                    {getApprovalTimeline(selectedRequest).map((item, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm">
                        {item.status === 'completed' ? (
                          <CheckCircle className="w-4 h-4 text-green-600" />
                        ) : item.status === 'rejected' ? (
                          <XCircle className="w-4 h-4 text-red-600" />
                        ) : (
                          <Clock className="w-4 h-4 text-yellow-600" />
                        )}
                        <span className={item.status === 'rejected' ? 'text-red-600' : ''}>
                          {item.label}
                          {item.date && ` - ${formatDate(item.date)}`}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {!selectedRequest.viewOnly && (
                <>
                  <div>
                    <Label>Notes (Optional)</Label>
                    <Textarea
                      value={approvalNotes}
                      onChange={(e) => setApprovalNotes(e.target.value)}
                      placeholder="Add any notes..."
                      rows={3}
                      data-testid="approval-notes"
                    />
                  </div>
                  
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" onClick={() => setSelectedRequest(null)}>
                      Cancel
                    </Button>
                    <Button
                      className={selectedRequest.action === 'approve' ? 'bg-green-600 hover:bg-green-700' : ''}
                      variant={selectedRequest.action === 'reject' ? 'destructive' : 'default'}
                      onClick={() => handleApproval(selectedRequest.id, selectedRequest.action)}
                      data-testid="confirm-approval-btn"
                    >
                      {selectedRequest.action === 'approve' ? 'Confirm Approval' : 'Confirm Rejection'}
                    </Button>
                  </div>
                </>
              )}
              
              {selectedRequest.viewOnly && (
                <Button className="w-full" onClick={() => setSelectedRequest(null)}>
                  Close
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SelfHelpPage;
